// AE Home dashboard (FR-P1) → docs/mockups/ae-dashboard.html. KPI cards, My Preprints by stage,
// My Students table. Central/Steph/Hildy/Admin open ANY AE via the staff roster (or the picker).
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DOMAINS, type AeDashboard, type Domain } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, CardBody, CardHeader, Input, Select, Spinner } from "../../components/ui.js";
import { KpiCard, PitchGroup, PreprintColumn } from "./dashboard-ui.js";

export function AeDashboardView({
  /** When set (Central/Admin drill-down), lock the view to this AE and hide the free-text picker. */
  forcedEditor,
}: {
  forcedEditor?: string;
} = {}): JSX.Element {
  const { user } = useAuth();
  const can = useCan();
  // FR-P1: only staff with the audit-trail capability may view ANOTHER AE's dashboard.
  const canViewOther = can("viewAuditTrail", {}) && !forcedEditor;
  const [editorInput, setEditorInput] = useState("");
  const [editor, setEditor] = useState<string | undefined>(undefined);

  const activeEditor = forcedEditor ?? editor;

  const { data, isLoading } = useQuery({
    queryKey: qk.dashboardAe(activeEditor),
    queryFn: () =>
      api.get<AeDashboard>(
        `/dashboard/ae${activeEditor ? `?editor=${encodeURIComponent(activeEditor)}` : ""}`,
      ),
  });

  const firstName = (data?.editor.name ?? user?.name)?.split(" ")[0] || "there";

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            {data?.viewingOther ? `${data.editor.name ?? data.editor.email}'s dashboard` : `Good day, ${firstName}! 👋`}
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data?.viewingOther ? "Viewing another AE's productivity." : "An overview of your editorial work."}
          </p>
        </div>
        {canViewOther && (
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setEditor(editorInput.trim() || undefined);
            }}
          >
            <label className="text-xs text-slate-500">
              View as AE
              <Input
                type="email"
                placeholder="ae@berkeley.edu"
                value={editorInput}
                onChange={(e) => setEditorInput(e.target.value)}
                className="mt-0.5 w-56"
              />
            </label>
            <Button type="submit" variant="secondary">
              Open
            </Button>
            {editor && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEditor(undefined);
                  setEditorInput("");
                }}
              >
                My dashboard
              </Button>
            )}
          </form>
        )}
      </header>

      {isLoading || !data ? (
        <Spinner label="Loading dashboard…" />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label="Greenlits this week"
              value={data.kpis.greenlitsThisWeek}
              tone="green"
              sublabel="Invited reviewers, last 7 days"
            />
            <KpiCard
              label="Greenlits to date"
              value={data.kpis.greenlitsToDate}
              tone="green"
              sublabel="On preprints you lead"
            />
            <KpiCard label="Proposals" value={data.kpis.proposals} tone="blue" sublabel="To date" />
            <KpiCard
              label="Greenlit proposals"
              value={data.kpis.greenlitProposals}
              tone="violet"
              sublabel="Reached Invite"
            />
          </div>

          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">📄 My Preprints</h2>
            </div>
            {/* R-003: Team + Editorial pitches merge into the single To-Pitch group. */}
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <PitchGroup team={data.preprints.toPitchTeam} editorial={data.preprints.toPitchEditorial} />
              <PreprintColumn title="Selected" cards={data.preprints.selected} tone="violet" />
              <PreprintColumn title="Active" cards={data.preprints.active} tone="green" showGreenlits />
            </div>
          </section>

          <StudentsRoster students={data.students} />

          {data.viewingOther && (
            <p className="text-xs text-slate-400">
              <Badge tone="amber">Viewing</Badge> You are viewing {data.editor.email}. Actions still
              respect your own permissions.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// R-018: the students roster with domain + team filters (course isn't captured on Users; these
// are the available fields — see the dashboard contract).
function StudentsRoster({ students }: { students: AeDashboard["students"] }): JSX.Element {
  const [domain, setDomain] = useState<Domain | "">("");
  const [team, setTeam] = useState("");
  const teams = useMemo(
    () => [...new Set(students.map((s) => s.team).filter((t): t is string => !!t?.trim()))].sort((a, b) => a.localeCompare(b)),
    [students],
  );
  const shown = students.filter(
    (s) => (!domain || s.domains.includes(domain)) && (!team || s.team === team),
  );
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">👥 My Students</h2>
        <div className="flex gap-2">
          <Select value={domain} onChange={(e) => setDomain(e.target.value as Domain | "")} aria-label="Filter students by domain">
            <option value="">All domains</option>
            {DOMAINS.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </Select>
          {teams.length > 0 && (
            <Select value={team} onChange={(e) => setTeam(e.target.value)} aria-label="Filter students by team">
              <option value="">All teams</option>
              {teams.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </Select>
          )}
        </div>
      </div>
      <Card>
        <CardHeader className="grid grid-cols-[1fr_auto_auto_auto] gap-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <span>Student</span>
          <span className="w-28 text-right">Domain / team</span>
          <span className="w-20 text-right">Proposals</span>
          <span className="w-20 text-right">Greenlits</span>
        </CardHeader>
        <CardBody className="space-y-1">
          {shown.length === 0 ? (
            <p className="text-sm text-slate-400">
              {students.length === 0
                ? "No students have proposed on your preprints yet."
                : "No students match these filters."}
            </p>
          ) : (
            shown.map((s) => (
              <div
                key={s.email}
                className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 rounded-md px-1 py-1.5 hover:bg-slate-50"
              >
                <span className="truncate text-sm">
                  {s.name ?? s.email}
                  {s.name && <span className="ml-1 text-xs text-slate-400">{s.email}</span>}
                </span>
                <span className="w-28 truncate text-right text-xs text-slate-500">
                  {[s.domains.join("/"), s.team].filter(Boolean).join(" · ") || "—"}
                </span>
                <span className="w-20 text-right text-sm">{s.proposals}</span>
                <span className="w-20 text-right text-sm font-semibold text-green-700">{s.greenlits}</span>
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </section>
  );
}
